<template>
  <a-layout-sider
    collapsible
    class="sider-primary dashboard-sider"
    breakpoint="lg"
    :collapsed-width="collapsedWidth"
    width="256px"
    :collapsed="sidebarCollapsed"
    @collapse="onCollapse"
    @breakpoint="onBreakpoint"
    :trigger="null"
    :class="[
      'ant-layout-sider-' + sidebarColorVal,
      'ant-layout-sider-' + sidebarThemeVal,
      {
        'dashboard-sider--collapsed': sidebarCollapsed,
        'dashboard-sider--mini': isMiniMode,
        'dashboard-sider--hidden': isHiddenMode,
      },
    ]"
    theme="light"
    :style="{ backgroundColor: 'transparent' }"
  >
    <div
      class="dashboard-sider__inner"
      :class="[
        `dashboard-sider__inner--${sidebarColorVal}`,
        `dashboard-sider__text--${sidebarTextColorVal}`,
        `dashboard-sider__selected--${sidebarSelectedTextColorVal}`,
      ]"
    >
      <div class="dashboard-sider__decor" aria-hidden="true" />

      <div class="dashboard-sider__brand" @click="goHome">
        <template v-if="isMiniMode">
          <img
            v-if="companyLogo"
            :src="companyLogo"
            :alt="companyName"
            class="dashboard-sider__brand-img"
          />
          <span v-else class="dashboard-sider__brand-mark">{{ companyInitial }}</span>
        </template>
        <div v-else class="dashboard-sider__brand-text">
          <span class="dashboard-sider__brand-title">{{ companyName }}</span>
          <span class="dashboard-sider__brand-tagline">{{ companyTagline }}</span>
        </div>
      </div>

      <hr class="dashboard-sider__divider" />

      <div class="dashboard-sider__content">
        <a-menu
          mode="inline"
          class="dashboard-sidebar-menu"
          v-model:openKeys="openKeys"
          v-model:selectedKeys="selectedKeys"
          :inline-collapsed="isMiniMode"
          :inline-indent="24"
          :items="antdMenuItems"
          @click="onAntMenuClick"
        />
      </div>

      <button type="button" class="dashboard-sider__back" @click="onBack">
        <i class="fa-solid fa-arrow-right-from-bracket dashboard-sider__back-icon" />
        <span v-if="!isMiniMode" class="dashboard-sider__back-label">{{ backLabel }}</span>
      </button>
    </div>
  </a-layout-sider>
</template>

<script lang="ts" setup>
import { ref, onMounted, onBeforeUnmount, watch, computed, h } from "vue";
import type { ItemType } from "ant-design-vue";
import { apiView } from "@/api/index";
import { ADMIN_SLIDE_MENU } from '@/const/ApiRedis'
import { useRouter } from "vue-router";
import { HOME } from '@/const/path'
import utils from "@/utils/Utils";
import commons from "@/utils/Common";
import DashboardMenuItem from "@/components/Sidebars/DashboardMenuItem.vue";

const emit = defineEmits(["toggleSidebar"]);

const LG_BREAKPOINT = 992;
const MINI_WIDTH = 72;

interface SlideMenuItem {
  id: number;
  parent: number;
  linkTo: string;
  linkLabel: string;
  icon?: string;
  quantity?: number | string | null;
  badge?: number | string | null;
  badgeCount?: number | string | null;
  count?: number | string | null;
  pendingCount?: number | string | null;
  children?: SlideMenuItem[];
}

interface Props {
  sidebarCollapsed: boolean;
  sidebarColor: string;
  sidebarTextColor: string;
  sidebarSelectedTextColor: string;
  sidebarTheme: string;
  backLabel?: string;
  backUrl?: string;
}

const propsData = withDefaults(defineProps<Props>(), {
  sidebarCollapsed: false,
  sidebarColor: "primary",
  sidebarTextColor: "primary",
  sidebarSelectedTextColor: "white",
  sidebarTheme: "light",
  backLabel: "Quay lại Trang chủ",
  backUrl: "",
});

const sidebarColorVal = computed(() => propsData.sidebarColor ?? "primary");
const sidebarTextColorVal = computed(() => propsData.sidebarTextColor ?? "primary");
const sidebarSelectedTextColorVal = computed(
  () => propsData.sidebarSelectedTextColor ?? "white"
);
const sidebarThemeVal = computed(() => propsData.sidebarTheme ?? "light");

const router = useRouter();
const menuList = ref<SlideMenuItem[]>([]);
const openKeys = ref<string[]>([]);
const selectedKeys = ref<string[]>([]);
const isMobile = ref(false);
const company = ref<Record<string, string>>({});

const activePath = computed(() => router.currentRoute.value.path);
const isMiniMode = computed(() => propsData.sidebarCollapsed && !isMobile.value);
const isHiddenMode = computed(() => propsData.sidebarCollapsed && isMobile.value);
const collapsedWidth = computed(() => (isMobile.value ? 0 : MINI_WIDTH));
const companyName = computed(
  () => company.value.sub_name || company.value.subName || "KIMAHOME"
);
const companyTagline = computed(
  () => company.value.summary || "LUXURY FURNITURE"
);
const companyLogo = computed(
  () => company.value.logo || company.value.urlLogo || ""
);
const companyInitial = computed(() => companyName.value.charAt(0).toUpperCase());

/** Always unique — avoids Ant Menu ghost titles when linkTo collides */
const menuItemKey = (item: SlideMenuItem) => `menu-${item.id}`;

const mapToAntdItems = (items: SlideMenuItem[], level = 0): ItemType[] =>
  items.map((item) => {
    const key = menuItemKey(item);
    const label = h(DashboardMenuItem, {
      item,
      showIcon: level === 0,
      iconOnly: isMiniMode.value && level === 0,
      isChild: level > 0,
    });

    if (item.children?.length) {
      return {
        key,
        label,
        children: mapToAntdItems(item.children, level + 1),
      };
    }

    return { key, label };
  });

const antdMenuItems = computed(() => mapToAntdItems(menuList.value));

const findMenuItemByKey = (
  items: SlideMenuItem[],
  key: string
): SlideMenuItem | null => {
  for (const item of items) {
    if (menuItemKey(item) === key) {
      return item;
    }
    if (item.children?.length) {
      const found = findMenuItemByKey(item.children, key);
      if (found) {
        return found;
      }
    }
  }
  return null;
};

const updateViewport = () => {
  isMobile.value = window.innerWidth < LG_BREAKPOINT;
};

const normalizeFlatMenu = (res: any): SlideMenuItem[] => {
  const payload = res?.data?.data ?? res?.data;
  if (Array.isArray(payload)) {
    return payload;
  }
  if (Array.isArray(payload?.body)) {
    return payload.body;
  }
  if (Array.isArray(res?.data?.body)) {
    return res.data.body;
  }
  const parsed = utils.parseJSON(payload);
  if (Array.isArray(parsed)) {
    return parsed;
  }
  if (Array.isArray(parsed?.body)) {
    return parsed.body;
  }
  return [];
};

const buildMenuTree = (flat: SlideMenuItem[]): SlideMenuItem[] => {
  const map = new Map<number, SlideMenuItem>();

  flat.forEach((item) => {
    map.set(item.id, { ...item, children: [] });
  });

  const roots: SlideMenuItem[] = [];

  flat.forEach((item) => {
    const node = map.get(item.id);
    if (!node) {
      return;
    }
    const parentId = Number(item.parent) || 0;
    if (parentId === 0) {
      roots.push(node);
      return;
    }
    const parent = map.get(parentId);
    if (parent?.children) {
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  });

  const trimEmptyChildren = (nodes: SlideMenuItem[]) => {
    nodes.sort((a, b) => a.id - b.id);
    nodes.forEach((node) => {
      if (node.children?.length) {
        trimEmptyChildren(node.children);
      } else {
        delete node.children;
      }
    });
  };

  trimEmptyChildren(roots);
  return roots;
};

const findOpenKeysForPath = (
  items: SlideMenuItem[],
  path: string,
  parents: string[] = []
): string[] | null => {
  for (const item of items) {
    if (item.linkTo === path) {
      return parents;
    }
    if (item.children?.length) {
      const found = findOpenKeysForPath(item.children, path, [
        ...parents,
        menuItemKey(item),
      ]);
      if (found !== null) {
        return found;
      }
    }
  }
  return null;
};

const syncMenuState = () => {
  const path = activePath.value;
  const matchKey =
    findMenuKeyByPath(menuList.value, path) ?? path;
  selectedKeys.value = [matchKey];

  const keys = findOpenKeysForPath(menuList.value, path);
  if (keys !== null) {
    openKeys.value = keys;
  }
};

const findMenuKeyByPath = (
  items: SlideMenuItem[],
  path: string
): string | null => {
  for (const item of items) {
    if (item.linkTo === path) {
      return menuItemKey(item);
    }
    if (item.children?.length) {
      const found = findMenuKeyByPath(item.children, path);
      if (found) {
        return found;
      }
    }
  }
  return null;
};

const loadData = async () => {
  const res: any = await apiView(ADMIN_SLIDE_MENU);
  const flat = normalizeFlatMenu(res);
  menuList.value = buildMenuTree(mockSlideQuantities(flat));
  syncMenuState();
};

/** Demo: random quantity for slide menu badges (0 / null → hidden, >999 → 999+) */
const mockSlideQuantities = (items: SlideMenuItem[]): SlideMenuItem[] => {
  // const rollQuantity = (): number | null => {
  //   const roll = Math.random();
  //   if (roll < 0.15) return null;
  //   if (roll < 0.25) return 0;
  //   // ~40% values > 999 to demo "999+"
  //   if (roll < 0.65) return Math.floor(Math.random() * 9000) + 1000;
  //   return Math.floor(Math.random() * 999) + 1;
  // };

  // return items.map((item) => ({
  //   ...item,
  //   quantity: rollQuantity(),
  // }));
  return  items;
};

const loadCompany = () => {
  const data = commons.getCokiesCompany();
  if (data) {
    company.value = data;
  }
};

const onMenuClick = (item: SlideMenuItem) => {
  if (item.linkTo?.trim()) {
    router.push(item.linkTo);
  }
};

const onAntMenuClick = (info: { key: string | number }) => {
  const item = findMenuItemByKey(menuList.value, String(info.key));
  if (item && !item.children?.length) {
    onMenuClick(item);
  }
};

const onCollapse = (collapsed: boolean) => {
  emit("toggleSidebar", collapsed);
};

const onBreakpoint = (broken: boolean) => {
  isMobile.value = broken;
  emit("toggleSidebar", broken);
};

const goHome = () => {
  router.push(HOME);
};

const onBack = () => {
  const url = propsData.backUrl?.trim();
  if (url) {
    window.location.href = url;
    return;
  }
  goHome();
};

onMounted(async () => {
  updateViewport();
  loadCompany();
  await loadData();
  window.addEventListener("resize", updateViewport);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", updateViewport);
});

watch(activePath, () => {
  syncMenuState();
});

watch(menuList, () => syncMenuState(), { deep: true });

watch(isMiniMode, (mini) => {
  if (mini) {
    openKeys.value = [];
  } else {
    syncMenuState();
  }
});
</script>

<style lang="scss" scoped>
$mini-width: 72px;

.dashboard-sider {
  position: relative;
  overflow: hidden !important;
  box-shadow: none !important;
  margin: 0 !important;
  padding: 0 !important;
  height: 100vh !important;
  border-radius: 0 !important;
  transition: all 0.2s ease;

  &.dashboard-sider--hidden {
    flex: 0 0 0 !important;
    max-width: 0 !important;
    min-width: 0 !important;
    width: 0 !important;
    overflow: hidden !important;
    border: none !important;
    pointer-events: none;
  }

  &.dashboard-sider--mini {
    flex: 0 0 $mini-width !important;
    max-width: $mini-width !important;
    min-width: $mini-width !important;
    width: $mini-width !important;
    pointer-events: auto;
  }
}

.dashboard-sider :deep(.ant-layout-sider-children) {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  width: 256px;
  transition: width 0.2s ease;
}

.dashboard-sider.dashboard-sider--mini :deep(.ant-layout-sider-children) {
  width: $mini-width !important;
}

.dashboard-sider.dashboard-sider--hidden :deep(.ant-layout-sider-children) {
  width: 0 !important;
  overflow: hidden !important;
  opacity: 0;
}

.dashboard-sider__inner {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 100%;
  padding: 20px 12px 16px;
  background: linear-gradient(
    165deg,
    #0a1829 0%,
    var(--primary-dark, #102542) 28%,
    #16365a 52%,
    #1a4268 72%,
    var(--primary-dark, #102542) 100%
  );
  border-right: 1px solid rgba(177, 150, 99, 0.2);
  color: var(--sidebar-text-color, var(--primary-gold, #b19663));
  overflow: hidden;
  transition: background 0.35s ease, border-color 0.35s ease;
}

.dashboard-sider__inner--primary {
  background: linear-gradient(
    165deg,
    #0a1829 0%,
    #102542 28%,
    #16365a 52%,
    #1a4268 72%,
    #102542 100%
  );
  border-right-color: rgba(177, 150, 99, 0.2);
}

.dashboard-sider__inner--secondary {
  background: linear-gradient(
    165deg,
    #2a1840 0%,
    #4b2a78 28%,
    #6b3fa0 55%,
    #4b2a78 100%
  );
  border-right-color: rgba(179, 127, 235, 0.35);
}

.dashboard-sider__inner--success {
  background: linear-gradient(
    165deg,
    #0f2a18 0%,
    #1b5e2f 28%,
    #2f8f4a 55%,
    #1b5e2f 100%
  );
  border-right-color: rgba(82, 196, 26, 0.35);
}

.dashboard-sider__inner--warning {
  background: linear-gradient(
    165deg,
    #2a2208 0%,
    #6b5610 28%,
    #b19663 55%,
    #6b5610 100%
  );
  border-right-color: rgba(250, 219, 20, 0.35);
}

.dashboard-sider__inner--danger {
  background: linear-gradient(
    165deg,
    #2a0c10 0%,
    #7a1520 28%,
    #c0392b 55%,
    #7a1520 100%
  );
  border-right-color: rgba(245, 34, 45, 0.35);
}

.dashboard-sider__inner--black {
  background: linear-gradient(
    165deg,
    #0a0a0a 0%,
    #141414 28%,
    #262626 55%,
    #141414 100%
  );
  border-right-color: rgba(255, 255, 255, 0.12);
}

/* Sidebar text color variants */
.dashboard-sider__text--primary {
  --sidebar-text-color: #b19663;
  --sidebar-text-muted: rgba(177, 150, 99, 0.72);
}

.dashboard-sider__text--white {
  --sidebar-text-color: #ffffff;
  --sidebar-text-muted: rgba(255, 255, 255, 0.72);
}

.dashboard-sider__text--secondary {
  --sidebar-text-color: #b37feb;
  --sidebar-text-muted: rgba(179, 127, 235, 0.75);
}

.dashboard-sider__text--success {
  --sidebar-text-color: #52c41a;
  --sidebar-text-muted: rgba(82, 196, 26, 0.75);
}

.dashboard-sider__text--warning {
  --sidebar-text-color: #fadb14;
  --sidebar-text-muted: rgba(250, 219, 20, 0.78);
}

.dashboard-sider__text--danger {
  --sidebar-text-color: #f5222d;
  --sidebar-text-muted: rgba(245, 34, 45, 0.75);
}

.dashboard-sider__text--black {
  --sidebar-text-color: #141414;
  --sidebar-text-muted: rgba(20, 20, 20, 0.7);
}

/* Selected text color variants */
.dashboard-sider__selected--primary {
  --sidebar-selected-text-color: #b19663;
}

.dashboard-sider__selected--white {
  --sidebar-selected-text-color: #ffffff;
}

.dashboard-sider__selected--secondary {
  --sidebar-selected-text-color: #b37feb;
}

.dashboard-sider__selected--success {
  --sidebar-selected-text-color: #52c41a;
}

.dashboard-sider__selected--warning {
  --sidebar-selected-text-color: #fadb14;
}

.dashboard-sider__selected--danger {
  --sidebar-selected-text-color: #f5222d;
}

.dashboard-sider__selected--black {
  --sidebar-selected-text-color: #141414;
}

.dashboard-sider__inner {
  color: var(--sidebar-text-color, var(--primary-gold, #b19663));
}

.dashboard-sider__decor {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  background:
    linear-gradient(
      145deg,
      transparent 0%,
      transparent 32%,
      rgba(26, 66, 104, 0.55) 42%,
      rgba(177, 150, 99, 0.1) 55%,
      rgba(10, 24, 41, 0.45) 68%,
      transparent 78%
    ),
    radial-gradient(
      ellipse 120% 80% at 0% 100%,
      rgba(22, 54, 90, 0.65) 0%,
      transparent 55%
    );
}

.dashboard-sider--mini .dashboard-sider__inner {
  align-items: center;
  padding: 16px 8px 12px;
}

.dashboard-sider__brand {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin: 0 0 4px;
  cursor: pointer;
  flex-shrink: 0;
}

.dashboard-sider--mini .dashboard-sider__brand {
  width: 44px;
  height: 44px;
  margin: 0 auto 12px;
}

.dashboard-sider__brand-text {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 4px 4px 8px;
  text-align: center;
}

.dashboard-sider__brand-title {
  font-family: var(--heading-font, "libre-baskerville", serif);
  font-size: 22px;
  font-weight: 700;
  line-height: 1.15;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-white, #fff);
  white-space: nowrap;
}

.dashboard-sider__brand-tagline {
  margin-top: 6px;
  font-family: var(--nav-font, "libre-baskerville", serif);
  font-size: 10px;
  font-weight: 400;
  line-height: 1.2;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: var(--sidebar-text-color, var(--primary-gold, #b19663));
  white-space: nowrap;
}

.dashboard-sider__divider {
  position: relative;
  z-index: 1;
  width: 100%;
  margin: 8px 0 12px;
  border: none;
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(177, 150, 99, 0.55) 20%,
    rgba(177, 150, 99, 0.55) 80%,
    transparent 100%
  );
  flex-shrink: 0;
}

.dashboard-sider--mini .dashboard-sider__divider {
  width: 28px;
  margin: 0 auto 12px;
}

.dashboard-sider__brand-img {
  width: 36px;
  height: 36px;
  object-fit: contain;
}

.dashboard-sider__brand-mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: linear-gradient(
    135deg,
    var(--primary-gold-light, #f4d03f) 0%,
    var(--default-header-color, #b19663) 100%
  );
  color: var(--primary-dark, #102542);
  font-size: 16px;
  font-weight: 700;
}

.dashboard-sider__content {
  position: relative;
  z-index: 1;
  flex: 1;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  width: 100%;
  padding-bottom: 4px;
  scrollbar-width: thin;
  scrollbar-color: rgba(177, 150, 99, 0.35) transparent;
}

.dashboard-sider__back {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  margin-top: 12px;
  padding: 10px 12px;
  border: 1px solid var(--sidebar-text-muted, rgba(177, 150, 99, 0.45));
  border-radius: 8px;
  background: transparent;
  color: var(--sidebar-text-color, var(--primary-gold, #b19663));
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  flex-shrink: 0;
  transition: background-color 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: rgba(177, 150, 99, 0.12);
    border-color: var(--sidebar-text-color, var(--primary-gold, #d4af37));
  }
}

.dashboard-sider--mini .dashboard-sider__back {
  width: 44px;
  height: 44px;
  padding: 0;
  border-radius: 10px;
}

.dashboard-sider__back-icon {
  font-size: 14px;
}

.dashboard-sider :deep(.dashboard-sidebar-menu) {
  border: none !important;
  border-inline-end: none !important;
  background: transparent !important;
  width: 100% !important;
  padding: 0 !important;
}

.dashboard-sider :deep(.dashboard-sidebar-menu .ant-menu-item),
.dashboard-sider :deep(.dashboard-sidebar-menu .ant-menu-submenu-title) {
  display: flex !important;
  align-items: center !important;
  min-height: 40px !important;
  height: 40px !important;
  line-height: 40px !important;
  margin: 0 !important;
  color: var(--sidebar-text-color, var(--primary-gold, #b19663)) !important;
  font-size: 14px;
  font-weight: 400;
  border: none !important;
  border-radius: 0 !important;
  width: 100% !important;
  box-shadow: none !important;
  outline: none !important;
  user-select: none !important;
  -webkit-user-select: none !important;
  transition: background-color 0.18s ease, color 0.18s ease;
}

/* Prevent gold ::selection flash + Ant click-wave on menu */
.dashboard-sider :deep(.dashboard-sidebar-menu),
.dashboard-sider :deep(.dashboard-sidebar-menu *) {
  user-select: none !important;
  -webkit-user-select: none !important;
}

.dashboard-sider :deep(.dashboard-sidebar-menu ::selection),
.dashboard-sider :deep(.dashboard-sidebar-menu *::selection) {
  background: transparent !important;
  color: inherit !important;
}

.dashboard-sider :deep([ant-click-animating-without-extra-node="true"]::after),
.dashboard-sider :deep(.ant-click-animating-node) {
  display: none !important;
  animation: none !important;
  opacity: 0 !important;
}

.dashboard-sider :deep(.dashboard-sidebar-menu .ant-menu-item::after),
.dashboard-sider :deep(.dashboard-sidebar-menu .ant-menu-item-selected::after),
.dashboard-sider :deep(.dashboard-sidebar-menu .ant-menu-item::before),
.dashboard-sider :deep(.dashboard-sidebar-menu .ant-menu-item-selected::before) {
  display: none !important;
  border: none !important;
  width: 0 !important;
  content: none !important;
}

/* Equal indent step (24px) — L2 vs L1 == L3 vs L2 */
$menu-indent-step: 24px;
$menu-indent-l1: 24px;
$menu-indent-l2: $menu-indent-l1 + $menu-indent-step; // 48px
$menu-indent-l3: $menu-indent-l1 + $menu-indent-step * 2; // 72px

/* Level 1 */
.dashboard-sider :deep(.dashboard-sidebar-menu.ant-menu-inline > .ant-menu-submenu > .ant-menu-submenu-title),
.dashboard-sider :deep(.dashboard-sidebar-menu > .ant-menu-submenu > .ant-menu-submenu-title),
.dashboard-sider :deep(.dashboard-sidebar-menu.ant-menu-inline > .ant-menu-item),
.dashboard-sider :deep(.dashboard-sidebar-menu > .ant-menu-item) {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  padding-left: $menu-indent-l1 !important;
  padding-right: 40px !important;
  background: transparent !important;
}

.dashboard-sider :deep(.dashboard-sidebar-menu.ant-menu-inline > .ant-menu-item) {
  padding-right: 16px !important;
}

.dashboard-sider :deep(.dashboard-sidebar-menu .ant-menu-submenu-title) {
  overflow: hidden !important;
}

.dashboard-sider :deep(.dashboard-sidebar-menu .ant-menu-title-content) {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.dashboard-sider :deep(.dashboard-sidebar-menu .dashboard-menu-item__icon) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  flex-shrink: 0;
  margin-inline-end: 10px;
  font-size: 14px;
  color: inherit;
  opacity: 0.95;
}

.dashboard-sider :deep(.dashboard-sidebar-menu .dashboard-menu-item__label-wrap) {
  flex: 1;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
}

.dashboard-sider :deep(.dashboard-sidebar-menu .dashboard-menu-item__label) {
  display: block;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dashboard-sider :deep(.dashboard-sidebar-menu .ant-menu-submenu-arrow) {
  color: var(--sidebar-text-muted, rgba(177, 150, 99, 0.65));
  inset-inline-end: 16px;
  font-size: 10px;
  opacity: 0.9;
}

.dashboard-sider :deep(.dashboard-sidebar-menu > .ant-menu-submenu),
.dashboard-sider :deep(.dashboard-sidebar-menu > .ant-menu-item) {
  margin: 0 !important;
  padding: 0 !important;
  border: none !important;
  background: transparent !important;
  width: 100% !important;
}

/* Nested group — soft panel; keep Ant padding-left from inline-indent */
.dashboard-sider :deep(.dashboard-sidebar-menu .ant-menu-sub) {
  background: rgba(255, 255, 255, 0.06) !important;
  padding: 0 !important;
  margin: 0 !important;
  border: none !important;
  box-shadow: none !important;
}

.dashboard-sider :deep(.dashboard-sidebar-menu .ant-menu-sub .ant-menu-item),
.dashboard-sider :deep(.dashboard-sidebar-menu .ant-menu-sub .ant-menu-submenu-title) {
  min-height: 40px !important;
  height: 40px !important;
  line-height: 40px !important;
  margin: 0 !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  padding-right: 40px !important;
  font-size: 14px;
  font-weight: 400;
  color: var(--sidebar-text-muted, rgba(177, 150, 99, 0.85)) !important;
  border-radius: 0 !important;
  width: 100% !important;
  max-width: 100% !important;
  border: none !important;
  box-shadow: none !important;
  background: transparent !important;
  box-sizing: border-box !important;
}

/* Level 2 — +24px from L1 */
.dashboard-sider
  :deep(
    .dashboard-sidebar-menu > .ant-menu-submenu > .ant-menu-sub > .ant-menu-item
  ),
.dashboard-sider
  :deep(
    .dashboard-sidebar-menu
      > .ant-menu-submenu
      > .ant-menu-sub
      > .ant-menu-submenu
      > .ant-menu-submenu-title
  ) {
  padding-left: $menu-indent-l2 !important;
}

/* Level 3 — +24px from L2 (same step as L2 vs L1) */
.dashboard-sider
  :deep(
    .dashboard-sidebar-menu
      > .ant-menu-submenu
      > .ant-menu-sub
      > .ant-menu-submenu
      > .ant-menu-sub
      > .ant-menu-item
  ),
.dashboard-sider
  :deep(
    .dashboard-sidebar-menu
      > .ant-menu-submenu
      > .ant-menu-sub
      > .ant-menu-submenu
      > .ant-menu-sub
      > .ant-menu-submenu
      > .ant-menu-submenu-title
  ) {
  padding-left: $menu-indent-l3 !important;
}

/* Level 4+ — keep same step */
.dashboard-sider
  :deep(
    .dashboard-sidebar-menu
      > .ant-menu-submenu
      > .ant-menu-sub
      > .ant-menu-submenu
      > .ant-menu-sub
      > .ant-menu-submenu
      > .ant-menu-sub
      > .ant-menu-item
  ),
.dashboard-sider
  :deep(
    .dashboard-sidebar-menu
      > .ant-menu-submenu
      > .ant-menu-sub
      > .ant-menu-submenu
      > .ant-menu-sub
      > .ant-menu-submenu
      > .ant-menu-sub
      > .ant-menu-submenu
      > .ant-menu-submenu-title
  ) {
  padding-left: ($menu-indent-l1 + $menu-indent-step * 3) !important;
}

.dashboard-sider :deep(.dashboard-sidebar-menu .ant-menu-sub .ant-menu-item) {
  padding-right: 16px !important;
}

.dashboard-sider :deep(.dashboard-sidebar-menu .ant-menu-sub .ant-menu-sub) {
  background: transparent !important;
}

.dashboard-sider :deep(.dashboard-sidebar-menu .ant-menu-item:hover),
.dashboard-sider :deep(.dashboard-sidebar-menu .ant-menu-submenu-title:hover),
.dashboard-sider :deep(.dashboard-sidebar-menu .ant-menu-item-active),
.dashboard-sider :deep(.dashboard-sidebar-menu .ant-menu-submenu-active > .ant-menu-submenu-title),
.dashboard-sider :deep(.dashboard-sidebar-menu .ant-menu-item:active),
.dashboard-sider :deep(.dashboard-sidebar-menu .ant-menu-submenu-title:active) {
  color: var(--sidebar-text-color, var(--primary-gold-light, #f4d03f)) !important;
  background: rgba(177, 150, 99, 0.1) !important;
}

.dashboard-sider :deep(.dashboard-sidebar-menu .ant-menu-item-selected),
.dashboard-sider :deep(.dashboard-sidebar-menu .ant-menu-item-selected:hover),
.dashboard-sider :deep(.dashboard-sidebar-menu .ant-menu-item-selected:active) {
  color: var(--sidebar-selected-text-color, #fff) !important;
  font-weight: 500;
  background: color-mix(
    in srgb,
    var(--sidebar-selected-text-color, #b19663) 16%,
    transparent
  ) !important;
  box-shadow: none !important;
  border: none !important;
  border-left: none !important;
  border-inline-start: none !important;
}

.dashboard-sider
  :deep(.dashboard-sidebar-menu .ant-menu-item-selected .dashboard-menu-item__icon),
.dashboard-sider
  :deep(.dashboard-sidebar-menu .ant-menu-item-selected .dashboard-menu-item__label) {
  color: var(--sidebar-selected-text-color, #fff) !important;
  opacity: 1;
}

.dashboard-sider
  :deep(.dashboard-sidebar-menu .ant-menu-item-selected .dashboard-menu-item__badge) {
  background: color-mix(
    in srgb,
    var(--sidebar-selected-text-color, #fff) 22%,
    transparent
  );
  color: var(--sidebar-selected-text-color, #fff);
}

.dashboard-sider :deep(.dashboard-sidebar-menu .ant-menu-submenu-selected > .ant-menu-submenu-title),
.dashboard-sider :deep(.dashboard-sidebar-menu .ant-menu-submenu-open > .ant-menu-submenu-title),
.dashboard-sider :deep(.dashboard-sidebar-menu .ant-menu-submenu-selected > .ant-menu-submenu-title:active),
.dashboard-sider :deep(.dashboard-sidebar-menu .ant-menu-submenu-open > .ant-menu-submenu-title:active) {
  color: var(--sidebar-text-color, var(--primary-gold-light, #f4d03f)) !important;
  background: transparent !important;
  font-weight: 400;
  box-shadow: none !important;
  border: none !important;
}

.dashboard-sider :deep(.dashboard-sidebar-menu .ant-menu-submenu-selected > .ant-menu-submenu-title:hover),
.dashboard-sider :deep(.dashboard-sidebar-menu .ant-menu-submenu-open > .ant-menu-submenu-title:hover) {
  background: rgba(177, 150, 99, 0.1) !important;
}

/* Open submenu panel — keep subtle, never solid gold */
.dashboard-sider :deep(.dashboard-sidebar-menu .ant-menu-sub .ant-menu-item:active),
.dashboard-sider :deep(.dashboard-sidebar-menu .ant-menu-sub .ant-menu-submenu-title:active),
.dashboard-sider :deep(.dashboard-sidebar-menu .ant-menu-sub .ant-menu-item-active) {
  background: rgba(177, 150, 99, 0.1) !important;
}

.dashboard-sider--mini :deep(.dashboard-sidebar-menu > .ant-menu-submenu),
.dashboard-sider--mini :deep(.dashboard-sidebar-menu > .ant-menu-item) {
  border-bottom: none;
}

/* Mini mode: icon-only tiles — highlight on icon, never on tall item bg */
.dashboard-sider--mini :deep(.dashboard-sidebar-menu),
.dashboard-sider--mini :deep(.dashboard-sidebar-menu.ant-menu-inline-collapsed) {
  display: flex !important;
  flex-direction: column;
  align-items: center;
  width: 100% !important;
}

.dashboard-sider--mini :deep(.ant-menu-item),
.dashboard-sider--mini :deep(.ant-menu-submenu),
.dashboard-sider--mini :deep(.ant-menu-submenu-title) {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  width: 44px !important;
  min-width: 44px !important;
  max-width: 44px !important;
  height: 44px !important;
  min-height: 44px !important;
  max-height: 44px !important;
  margin: 6px auto !important;
  padding: 0 !important;
  line-height: 44px !important;
  border-radius: 12px !important;
  overflow: hidden !important;
  box-shadow: none !important;
  background: transparent !important;
  left: auto !important;
}

.dashboard-sider--mini :deep(.ant-menu-submenu) {
  margin: 6px auto !important;
}

.dashboard-sider--mini :deep(.ant-menu-submenu > .ant-menu-submenu-title) {
  margin: 0 !important;
}

.dashboard-sider--mini :deep(.ant-menu-title-content) {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  width: 100% !important;
  height: 100% !important;
  margin: 0 !important;
  line-height: 1 !important;
  overflow: hidden !important;
}

.dashboard-sider--mini :deep(.dashboard-menu-item) {
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  gap: 0;
  overflow: hidden;
}

.dashboard-sider--mini :deep(.dashboard-menu-item__icon) {
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  width: 40px !important;
  height: 40px !important;
  margin: 0 !important;
  border-radius: 10px;
  font-size: 18px !important;
  line-height: 1 !important;
  opacity: 1;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.dashboard-sider--mini :deep(.dashboard-menu-item__label),
.dashboard-sider--mini :deep(.dashboard-menu-item__badge),
.dashboard-sider--mini :deep(.ant-menu-submenu-arrow),
.dashboard-sider--mini :deep(.ant-menu-submenu-arrow::before),
.dashboard-sider--mini :deep(.ant-menu-submenu-arrow::after) {
  display: none !important;
  width: 0 !important;
  height: 0 !important;
  margin: 0 !important;
  padding: 0 !important;
  overflow: hidden !important;
}

.dashboard-sider--mini :deep(.ant-menu-item::after),
.dashboard-sider--mini :deep(.ant-menu-item-selected::after),
.dashboard-sider--mini :deep(.ant-menu-submenu-title::after) {
  display: none !important;
  border: none !important;
  width: 0 !important;
  height: 0 !important;
  content: none !important;
}

/* Selected: only the icon tile gets highlight — no item background “tail” */
.dashboard-sider--mini :deep(.ant-menu-item-selected),
.dashboard-sider--mini :deep(.ant-menu-submenu-selected),
.dashboard-sider--mini :deep(.ant-menu-submenu-selected > .ant-menu-submenu-title) {
  background: transparent !important;
  box-shadow: none !important;
  border: none !important;
}

.dashboard-sider--mini :deep(.ant-menu-item-selected .dashboard-menu-item__icon),
.dashboard-sider--mini
  :deep(.ant-menu-submenu-selected > .ant-menu-submenu-title .dashboard-menu-item__icon) {
  background: color-mix(
    in srgb,
    var(--sidebar-selected-text-color, #ffffff) 22%,
    transparent
  ) !important;
  color: var(--sidebar-selected-text-color, #fff) !important;
  opacity: 1 !important;
}

.dashboard-sider--mini :deep(.ant-menu-item:hover),
.dashboard-sider--mini :deep(.ant-menu-submenu-title:hover) {
  background: transparent !important;
}

.dashboard-sider--mini :deep(.ant-menu-item:hover .dashboard-menu-item__icon),
.dashboard-sider--mini :deep(.ant-menu-submenu-title:hover .dashboard-menu-item__icon) {
  background: rgba(255, 255, 255, 0.08) !important;
}

.dashboard-sider--mini :deep(.ant-menu-sub),
.dashboard-sider--mini :deep(.ant-menu-submenu .ant-menu) {
  display: none !important;
  height: 0 !important;
  max-height: 0 !important;
  overflow: hidden !important;
  padding: 0 !important;
  margin: 0 !important;
  opacity: 0 !important;
  visibility: hidden !important;
}
</style>
